import { HttpHeaders } from "@angular/common/http";
import { Injectable, inject } from "@angular/core";
import { Observable } from "rxjs";

import { BaseServiceExt } from "@providers/BaseServiceExt";
import { XStateService } from "@sports-services/index";
import { SportsConstants } from "@sports-utils/index";
import {
  C_FreeBetVoucher,
  CombinationMulticastInput,
  OddItem,
  SingleItemInput,
  SmartMarketInput,
  StakeModel,
  StraightMulticastInput,
  removeTicketModel,
} from "@ticket/models";

@Injectable({ providedIn: "root" })
export class BetSlipService extends BaseServiceExt {
  private hideLoader = false;
  private xStateService = inject(XStateService);

  // Set HTTP Options
  private getHttpOptions(extraHeaders: Record<string, string> = {}): { observe: string; headers: HttpHeaders } {
    return {
      observe: "response",
      headers: new HttpHeaders({
        "X-State": this.xStateService.getXState(),
        ...extraHeaders,
      }),
    };
  }

  // Single Result
  addSingleResult(bet: SingleItemInput): Observable<any> {
    const url = `${this.globalVars.BETSLIPSERVICEBASEURL}${SportsConstants.addSingleItemUrl}`;
    return this.myPost2(url, bet, this.getHttpOptions(), this.hideLoader);
  }

  // Betbuilder
  addSmartMarketItem(bet: SmartMarketInput): Observable<any> {
    const url = `${this.globalVars.BETSLIPSERVICEBASEURL}${SportsConstants.addSmartMarketItemUrl}`;
    return this.myPost2(url, bet, this.getHttpOptions(), this.hideLoader);
  }

  addSmartMarket(bet: SingleItemInput): Observable<any> {
    const url = `${this.globalVars.BETSLIPSERVICEBASEURL}${SportsConstants.smartMarketUrl}`;
    return this.myPost2(url, bet, this.getHttpOptions(), this.hideLoader);
  }

  // Races Result
  addStraightMulticast(bet: StraightMulticastInput): Observable<any> {
    const url = `${this.globalVars.BETSLIPSERVICEBASEURL}${SportsConstants.straightMulticast}`;
    return this.myPost2(url, bet, this.getHttpOptions(), this.hideLoader);
  }

  addCombinationMulticast(bet: CombinationMulticastInput): Observable<any> {
    const url = `${this.globalVars.BETSLIPSERVICEBASEURL}${SportsConstants.combinationMulticast}`;
    return this.myPost2(url, bet, this.getHttpOptions(), this.hideLoader);
  }

  removeItem(id: number): Observable<any> {
    const url = this.globalVars.BETSLIPSERVICEBASEURL + SportsConstants.removeItemUrl + id;
    return this.myDelete2(url, this.getHttpOptions());
  }

  removeItems(ids: number[]): Observable<any> {
    const encodedIds = ids.map((id) => `${encodeURIComponent(id)}`).join(",");
    const url = `${this.globalVars.BETSLIPSERVICEBASEURL}${SportsConstants.removeItemUrl}${encodedIds}`;

    return this.myDelete2(url, this.getHttpOptions());
  }

  // Odds
  updateOdds(oddsItems: OddItem[]): Observable<any> {
    const url = `${this.globalVars.BETSLIPSERVICEBASEURL}${SportsConstants.setOdds}`;
    const dataOdds = { odds: oddsItems };
    return this.myPatch2(url, dataOdds, this.getHttpOptions(), true);
  }

  // Stake
  stake(stk: StakeModel): Observable<any> {
    const url = `${this.globalVars.BETSLIPSERVICEBASEURL}${SportsConstants.stakeUrl}${stk.Group}`;
    const stakeData = { ...stk };
    delete stakeData.Group;
    delete stakeData.LineItems;
    delete stakeData.MultiSingleAmount;
    return this.myPost2(url, stakeData, this.getHttpOptions(), this.hideLoader);
  }

  removeStake(stakeGroup: string): Observable<any> {
    const url = `${this.globalVars.BETSLIPSERVICEBASEURL}${SportsConstants.stakeUrl}${stakeGroup}`;
    return this.myDelete2(url, this.getHttpOptions());
  }

  // Betting
  placeBet(groupKey: string, validateBonus: boolean = false): Observable<any> {
    const url = `${this.globalVars.BETSLIPSERVICEBASEURL}${SportsConstants.stakeUrl}${groupKey}/tickets`;
    const headers = validateBonus
      ? { "X-Validate-Bonus-Offers": "true", Authorization: this.xStateService.getAuthorization() }
      : {};
    return this.myGet2(url, this.getHttpOptions(headers), this.hideLoader);
  }

  closeBet(groupKey: string): Observable<Response> {
    const { AutoAcceptOddUpChanges, AutoAcceptOddDownChanges } = this.globalVars.user.customization;
    const url = `${this.globalVars.URLBASE_BETPLACEMENT}TicketsAndCloseBet?groupKey=${groupKey}`;
    const headers = {
      AutoAcceptOddUpChanges: AutoAcceptOddUpChanges.toString(),
      AutoAcceptOddDownChanges: AutoAcceptOddDownChanges.toString(),
      language: this.globalVars.Idioma,
    };
    return this.myGet2(url, this.getHttpOptions(headers), this.hideLoader);
  }

  removeTicket(removeT: removeTicketModel): Observable<any> {
    const url = `${this.globalVars.BETSLIPSERVICEBASEURL}${SportsConstants.removeTicket}`;
    return this.myPost2(url, removeT, this.getHttpOptions());
  }

  // Freebet
  freebetAdd(voucher: C_FreeBetVoucher): Observable<any> {
    const url = `${this.globalVars.BETSLIPSERVICEBASEURL}${SportsConstants.freeBetAdd}`;
    return this.myPost2(url, voucher, this.getHttpOptions(), false);
  }

  freebetCancel(voucherCode: string): Observable<any> {
    const url = `${this.globalVars.BETSLIPSERVICEBASEURL}${SportsConstants.freeBetCancel}/${voucherCode}`;
    return this.myDelete2(url, this.getHttpOptions());
  }

  freebetValidate(): Observable<any> {
    const url = `${this.globalVars.BETSLIPSERVICEBASEURL}${SportsConstants.freeBetValidate}`;
    return this.myGet2(url, this.getHttpOptions());
  }

  overaskCheckState(OveraskRejectId: any): Observable<any> {
    const url = `${this.globalVars.URLBASE_BETPLACEMENT}api/Overask?rejectId=${OveraskRejectId}`;
    return this.myGet2(url, false);
  }

  // Other
  getLicenseSessionHeader(): Observable<any> {
    const licenseType = this.globalVars.licenseType;
    const url = `${this.globalVars.URLBASE_BETPLACEMENT}AuthorizationHeader?licenseType=${licenseType}`;
    return this.myGet(url, false);
  }
}
